// src/components/sla/ai/SlaScoreboard.js
// ─────────────────────────────────────────────────────────────────────────────
// Tech / contractor SLA reliability ranking from graded work (model sla-grade-1.0):
// reliability (Bayesian-shrunk), on-time %, average grade, breaches in 30 days,
// jobs moved away by the AI, grade mix, trend; expand a row for recent graded jobs.
// ─────────────────────────────────────────────────────────────────────────────
import React, { Fragment, memo, useCallback, useMemo, useState } from 'react';
import {
    Alert, AlertDescription, AlertIcon, Badge, Box, Button, ButtonGroup, Collapse, Flex, HStack, Icon, IconButton, Input,
    InputGroup, InputLeftElement, Progress, Select, Skeleton, Stack, Table, Tbody, Td, Text, Th, Thead, Tooltip, Tr,
    useColorModeValue,
} from '@chakra-ui/react';
import { MdExpandLess, MdExpandMore, MdLeaderboard, MdRefresh, MdSearch, MdTrendingDown, MdTrendingFlat, MdTrendingUp, MdFiberNew } from 'react-icons/md';
import { useSlaScoreboard } from '../../../hooks/useSlaAi';
import SlaGradeBadge from './SlaGradeBadge';
import { GRADE_SCHEME, OUTCOME_META, fmtMinutes, fmtRelative, pct, scoreScheme } from './slaAiFormat';

const TREND = { improving: [MdTrendingUp, 'green', 'Improving'], declining: [MdTrendingDown, 'red', 'Declining'], steady: [MdTrendingFlat, 'gray', 'Steady'], new: [MdFiberNew, 'blue', 'New — few graded jobs'] };

const GradeMix = memo(function GradeMix({ mix = {} }) {
    const total = Object.values(mix).reduce((s, n) => s + n, 0);
    if (!total) return <Text fontSize="xs" color="gray.500">—</Text>;
    return (
        <Tooltip label={Object.entries(mix).map(([g, n]) => `${g}: ${n}`).join(' · ')} hasArrow>
            <Flex h="8px" w="96px" borderRadius="full" overflow="hidden" aria-label="Grade mix">
                {Object.entries(mix).filter(([, n]) => n > 0).map(([g, n]) => <Box key={g} bg={`${GRADE_SCHEME[g]}.400`} w={`${(n / total) * 100}%`} />)}
            </Flex>
        </Tooltip>
    );
});

function SlaScoreboard({ defaultType = 'all', days: initialDays = 90 }) {
    const [type, setType] = useState(defaultType);
    const [days, setDays] = useState(initialDays);
    const [q, setQ] = useState('');
    const [open, setOpen] = useState(null);
    const board = useSlaScoreboard({ type, days });
    const muted = useColorModeValue('gray.500', 'gray.400');
    const expandBg = useColorModeValue('gray.50', 'whiteAlpha.50');
    const { refresh } = board;

    const rows = useMemo(() => {
        const list = board.data || [];
        const s = q.trim().toLowerCase();
        return s ? list.filter(r => String(r.name || '').toLowerCase().includes(s)) : list;
    }, [board.data, q]);
    const toggle = useCallback((key) => setOpen(o => (o === key ? null : key)), []);

    return (
        <Box>
            <Flex justify="space-between" align="center" mb={3} gap={2} flexWrap="wrap">
                <HStack><Icon as={MdLeaderboard} color="purple.500" /><Text fontWeight="800">SLA scoreboard</Text></HStack>
                <HStack spacing={2} flexWrap="wrap">
                    <ButtonGroup size="sm" isAttached variant="outline">
                        {['all', 'tech', 'contractor'].map(t => <Button key={t} onClick={() => setType(t)} colorScheme={type === t ? 'purple' : 'gray'} variant={type === t ? 'solid' : 'outline'}>{t === 'all' ? 'All' : t === 'tech' ? 'Technicians' : 'Contractors'}</Button>)}
                    </ButtonGroup>
                    <Select size="sm" w="auto" value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Period" borderRadius="md">
                        {[30, 90, 180, 365].map(n => <option key={n} value={n}>Last {n} days</option>)}
                    </Select>
                    <InputGroup size="sm" w="180px">
                        <InputLeftElement pointerEvents="none"><MdSearch /></InputLeftElement>
                        <Input placeholder="Search name" value={q} onChange={(e) => setQ(e.target.value)} borderRadius="md" />
                    </InputGroup>
                    <IconButton size="sm" variant="ghost" icon={<MdRefresh />} aria-label="Refresh scoreboard" onClick={refresh} isLoading={board.refreshing} />
                </HStack>
            </Flex>

            {board.error && (
                <Alert status="error" borderRadius="lg" mb={3}><AlertIcon /><AlertDescription flex="1" fontSize="sm">{board.error}</AlertDescription><Button size="xs" onClick={() => board.reload()}>Retry</Button></Alert>
            )}
            {board.loading ? (
                <Stack spacing={2}>{[0, 1, 2, 3].map(i => <Skeleton key={i} h="44px" borderRadius="md" />)}</Stack>
            ) : !board.error && rows.length === 0 ? (
                <Box textAlign="center" py={8} color={muted}><Text fontWeight="600">No graded work yet.</Text><Text fontSize="sm">Scores appear as jobs with an SLA are completed or breached.</Text></Box>
            ) : (
                <Box overflowX="auto" borderWidth="1px" borderRadius="xl">
                    <Table size="sm">
                        <Thead>
                            <Tr><Th w="1%">#</Th><Th>Who</Th><Th>Reliability</Th><Th isNumeric>On time</Th><Th isNumeric>Avg</Th><Th isNumeric>Jobs</Th><Th isNumeric>Breaches 30d</Th><Th isNumeric>Moved away</Th><Th>Grades</Th><Th>Trend</Th><Th w="1%" /></Tr>
                        </Thead>
                        <Tbody>
                            {rows.map((r) => {
                                const [TIcon, tScheme, tLabel] = TREND[r.trend] || TREND.steady;
                                const isOpen = open === r.key;
                                return (
                                    <Fragment key={r.key}>
                                        <Tr _hover={{ bg: expandBg }} cursor="pointer" onClick={() => toggle(r.key)}>
                                            <Td fontWeight="700">{r.rank}</Td>
                                            <Td><Text fontWeight="700" fontSize="sm">{r.name || r.key}</Text><Badge variant="subtle" colorScheme={r.type === 'contractor' ? 'orange' : 'blue'}>{r.type}</Badge></Td>
                                            <Td minW="160px">
                                                <HStack spacing={2}><SlaGradeBadge grade={r.grade} size="sm" /><Box flex="1"><Progress value={r.reliability} size="sm" borderRadius="full" colorScheme={scoreScheme(r.reliability)} /><Text fontSize="2xs" color={muted}>{r.reliability}</Text></Box></HStack>
                                            </Td>
                                            <Td isNumeric>{pct(r.on_time_rate)}</Td>
                                            <Td isNumeric>{r.avg_score ?? '—'}</Td>
                                            <Td isNumeric>{r.jobs}</Td>
                                            <Td isNumeric>{r.breaches_30d ? <Badge colorScheme={r.major_breaches_30d ? 'red' : 'orange'}>{r.breaches_30d}</Badge> : 0}</Td>
                                            <Td isNumeric>{r.reassigned_away || 0}</Td>
                                            <Td><GradeMix mix={r.grade_mix} /></Td>
                                            <Td><Tooltip label={tLabel} hasArrow><span><Icon as={TIcon} color={`${tScheme}.400`} boxSize={5} aria-label={tLabel} /></span></Tooltip></Td>
                                            <Td><Icon as={isOpen ? MdExpandLess : MdExpandMore} /></Td>
                                        </Tr>
                                        <Tr>
                                            <Td colSpan={11} p={0} borderBottomWidth={isOpen ? '1px' : 0}>
                                                <Collapse in={isOpen} animateOpacity>
                                                    <Box bg={expandBg} px={4} py={3}>
                                                        <Text fontSize="xs" fontWeight="800" color={muted} mb={2}>RECENT GRADED WORK</Text>
                                                        {(r.recent || []).length === 0 ? <Text fontSize="sm" color={muted}>Nothing recent.</Text> : (
                                                            <Stack spacing={1}>
                                                                {r.recent.map((e) => (
                                                                    <HStack key={`${e.work_order_id}-${e.event_type}`} spacing={3} fontSize="sm">
                                                                        <SlaGradeBadge grade={e.grade} score={e.score} size="sm" projected={e.event_type === 'breach'} />
                                                                        <Text>WO #{e.work_order_id}</Text>
                                                                        <Badge colorScheme={(OUTCOME_META[e.outcome] || {}).scheme || 'gray'}>{(OUTCOME_META[e.outcome] || {}).label || e.outcome}</Badge>
                                                                        {e.event_type === 'breach' && <Badge colorScheme="purple" variant="outline">moved by AI</Badge>}
                                                                        {e.minutes_late ? <Text color={muted}>{fmtMinutes(e.minutes_late)} late</Text> : null}
                                                                        <Text color={muted}>{fmtRelative(e.created_at)}</Text>
                                                                    </HStack>
                                                                ))}
                                                            </Stack>
                                                        )}
                                                    </Box>
                                                </Collapse>
                                            </Td>
                                        </Tr>
                                    </Fragment>
                                );
                            })}
                        </Tbody>
                    </Table>
                </Box>
            )}
        </Box>
    );
}

export default memo(SlaScoreboard);
